// file-share-browser.js
// Browser-only wrapper: Web Crypto hashing + Blob URLs instead of
// node:crypto + disk writes. Same pattern as file-share.js (Node's
// wrapper) — the actual chunking/reassembly/offer logic lives in
// file-share-core.js and is never duplicated between platforms.
//
// Design choice worth noting: writeOutput() does NOT trigger an
// automatic download. It returns a Blob URL and leaves the decision of
// what to do with it (show a download link, display inline if it's an
// image, auto-download) to the UI. Two reasons: forcing a download the
// instant a transfer completes is often unwanted UX, and some browsers
// block programmatic downloads that aren't triggered by a direct user
// gesture (a click), which an automatic call from deep inside a network
// event handler is not.
//
// Reading a file to send: browsers don't have filesystem paths, they
// have File/Blob objects (from an <input type=file> or drag-drop) — so
// offerBrowserFile() takes a File directly, unlike file.js's
// offerFile(path).

import { FileShareManagerCore, DEFAULT_CHUNK_SIZE } from '../file-share-core.js'

async function sha256Hex (bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function browserAdapter () {
  return {
    hashHex: sha256Hex,
    toBase64 (bytes) {
      // Buffer is polyfilled at bundle time (see scripts/buffer-shim.js)
      // — same approach used throughout this project's browser bundle,
      // rather than hand-rolling base64 encoding separately here.
      return Buffer.from(bytes).toString('base64')
    },
    fromBase64 (base64) {
      return new Uint8Array(Buffer.from(base64, 'base64'))
    },
    async writeOutput (filename, bytes) {
      const blob = new Blob([bytes])
      const blobUrl = URL.createObjectURL(blob)
      return { blobUrl, size: bytes.length }
    }
  }
}

export class FileShareManager extends FileShareManagerCore {
  /**
   * @param {(messageText: string) => void | Promise<void>} sendFn
   * @param {(result: object) => void} onFileReceived - result.blobUrl is
   *   set on success instead of result.path (there's no filesystem path
   *   in a browser) — pass it directly as an <a href> or <img src>.
   * @param {{chunkSize?: number}} [opts]
   */
  constructor (sendFn, onFileReceived, opts = {}) {
    super(sendFn, onFileReceived, browserAdapter(), { chunkSize: opts.chunkSize ?? DEFAULT_CHUNK_SIZE })
  }

  /**
   * Read, hash, chunk, and send a File object (from an <input type=file>
   * or drag-drop event) — the browser equivalent of file-share.js's
   * offerFile(path), which takes a filesystem path instead since that
   * concept doesn't exist here.
   * @param {File} file
   */
  async offerBrowserFile (file) {
    const buffer = await file.arrayBuffer()
    return this.offerBytes(file.name, new Uint8Array(buffer))
  }
}
